import { useCallback, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { AVAILABLE_TIMES } from "../../services/available-times/available-times.query";
import {
  IAvailableTimesResponse,
} from "../../services/available-times/available-times.interface";
import {
  availableTimesMap,
  daysOfMonthMap,
} from "../../services/available-times/available-times.service";
import { IOptionGeneric } from "../../shared/interface";
import {
  useUrgentVisitStore,
} from "../redux-hooks/urgent-visit-store/urgent-visit.store";
import { getUnixTimestamp } from "../../utils";
import {
  ILocationItemResponse,
} from "../../services/edit-hospital-location/edit-hospital-location.interface";

export const useUrgentVisitAvailableTimes = () => {
  const {
    selectedPets, homeLocationId, selectedSlot, selectedLocationId,
    selectedDate, setTimezone, setSelectedSlot,
  } = useUrgentVisitStore();
  const [timeSlots, setTimeSlots] = useState<IOptionGeneric[]>([]);
  const [fullMonthDaysList, setFullMonthDaysList] = useState<IOptionGeneric[]>([]);
  const [locationDetails, setLocationDetails] =
    useState<ILocationItemResponse | null>(null);

  const selectedDateEpoch = selectedDate ?? getUnixTimestamp(new Date());

  const [fetchAvailableTimes, { loading }] = useLazyQuery<{
    availableTimesV2: IAvailableTimesResponse;
  }>(AVAILABLE_TIMES, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.availableTimesV2) {
        const { availableTimes, timeZone, location, daysMonth } =
          data.availableTimesV2;
        const mapped = availableTimesMap(availableTimes.times, timeZone);

        setTimeSlots(mapped);
        setTimezone(timeZone);
        if (daysMonth) {
          setFullMonthDaysList(daysOfMonthMap(daysMonth));
        }

        if (location) {
          setLocationDetails({
            ...(location as unknown as ILocationItemResponse),
            timeZone,
          });
        }

        if (
          selectedSlot &&
          selectedLocationId === homeLocationId &&
          !mapped.some((s) => s.value === selectedSlot.time)
        ) {
          setSelectedSlot(null);
        }
      }
    },
    onError: () => {
      setTimeSlots([]);
    },
  });

  const loadHomeHospitalSlots = useCallback(() => {
    if (!homeLocationId) return;

    const pets = selectedPets.map((pet) => ({
      petId: pet.id,
      reasonsForVisit: "",
    }));

    fetchAvailableTimes({
      variables: {
        data: {
          pets,
          date: selectedDateEpoch.toString(),
          locationId: Number(homeLocationId),
        },
      },
    });
  }, [homeLocationId, selectedPets, selectedDateEpoch, fetchAvailableTimes]);

  return {
    timeSlots,
    loading,
    locationDetails,
    loadHomeHospitalSlots,
    selectedDateEpoch,
    fullMonthDaysList,
  };
};
